import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { DashboardProfileContextService } from '@rma/dashboard/authentication/public/user';
import { StandardErrorMessage } from '@rma/dashboard/generic/domain/to-be-sorted';
import { MatErrorOverrideComponent } from '@rma/dashboard/generic/ui/form-error';
import { ToastService } from '@rma/dashboard/generic/ui/toast';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { combineLatest, distinctUntilChanged, filter, Observable, Subject, switchMap, take } from 'rxjs';
import { BillingDetailsService } from '../domain/data-access/billing-details.service';
import { BillingDetails } from '../domain/entities/billing-details';

interface BillingContactForm {
  email: string;
}

@Component({
  selector: 'rma-update-billing-contact-details',
  templateUrl: './update-billing-contact-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    MatButtonModule,
    MatDialogModule,
    MatDividerModule,
    MatErrorOverrideComponent,
    RmaIconComponent,
    MatInputModule,
    ReactiveFormsModule,
    TranslocoModule,
  ],
  providers: [provideTranslocoScope('subscriptions')],
})
export class UpdateBillingContactDetailsComponent implements OnInit {
  public form: UntypedFormGroup;

  public error$ = new Subject<string | undefined>();

  private profileCode: Observable<string>;
  private isAgency: Observable<boolean>;

  public constructor(
    @Inject(MAT_DIALOG_DATA) public data: string,
    private readonly billingDetailsService: BillingDetailsService,
    private readonly dialogRef: MatDialogRef<UpdateBillingContactDetailsComponent>,
    private readonly fb: UntypedFormBuilder,
    private readonly profileContextService: DashboardProfileContextService,
    private readonly toastService: ToastService,
  ) {
    this.form = this.fb.group({
      email: [null, [Validators.required, Validators.email]],
    });
    this.profileCode = this.profileContextService.profileCode$.pipe(distinctUntilChanged());
    this.isAgency = this.profileContextService.isAgency$;
  }

  public ngOnInit(): void {
    const formValue: BillingContactForm = { email: this.data };
    this.form.patchValue(formValue, { emitEvent: true });
  }

  public onSubmit(form: BillingContactForm, isValid: boolean) {
    if (!isValid) {
      return;
    }

    this.error$.next(undefined);

    const model: BillingDetails = {
      billingEmailAddress: form.email,
    };

    combineLatest([this.isAgency, this.profileCode])
      .pipe(
        take(1),
        filter(([isAgency, _]) => isAgency),
        switchMap(([_, code]) => this.billingDetailsService.updateAgencyDetails(model, code)),
      )
      .subscribe({
        next: () => {
          this.toastService.showSuccessToast({
            key: 'subscriptions.billing-details.saved',
          });
          this.dialogRef.close(form.email);
        },
        error: (err) => this.error$.next(err.error?.message ?? StandardErrorMessage),
      });
  }
}
